import React from 'react';
import {
  AppMode,
  AvailabilityStatus,
  AvatarConfig,
  ChatWidget,
  expandCollapseManager,
} from '@wix/chat-web';
import serverApi from '../../../services/serverApi';
import wix from '../../../services/wix';
import firebaseChatService from '../../../services/firebaseChatService';
import { EditorViewMode } from '../../../constants/editor-view-mode';
import * as biLogger from '../../../services/biLogger';
import { mediaManager, ON_NEW_MESSAGE } from '@wix/inbox-common';
import withMobile from '../../../hoc/mobile';
import { withExperiments } from '@wix/wix-experiments-react';
import { CTA_PRODUCT } from '../../../constants/call-to-action';
import {
  editorSettingsAvatar,
  isMessageNotificationEnabled,
  theme,
} from '../../../reducers';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import {
  collapseWidget,
  expandWidget,
  firstExpand,
  goOffline,
  updateHasMessages,
} from '../../../actions';
import { initApp } from '../../../actions/serverApi';
import { chatSdk, Message } from '@wix/chat-sdk';
import { LazyChatRoomScreen } from './LazyChatRoomScreen';
import { loadingScheduler } from '../../../services/loading-scheduler';
import { MessagePopup } from '../../MessagePopup/MessagePopup';
import { ViewerOnMessageSentEvent } from '../../../constants/viewer-event-types';
import * as Sentry from '@sentry/browser';

interface BusinessOnlyWidgetProps {
  visibilitySettings: any;
  themeData: any;
  viewMode: EditorViewMode;
  locale: string;
  experiments: any;
  isMobileOpen: boolean;
  onMessageReceived: Function;
  editorSettingsLayout: any;
  editorSettingsTexts: any;
  editorSettingsBehaviour: any;
  editorSettingsAvatar: any;
  firebaseGoOffline: Function;
  initApp: Function;
  shardId: string;
  businessChatRoomId: string;
  isChatSdkInit: boolean;
  deviceType: string;
  availabilityStatus: AvailabilityStatus;
  isBranded: boolean;
  widgetExpanded: boolean;
  expandWidget: Function;
  collapseWidget: Function;
  hasMessages: boolean;
  updateHasMessages: Function;
  widgetMode: string;
  firstExpand: Function;
  isContact: boolean;
  unreadCount: number;
  isMessageNotificationEnabled: boolean;
}

interface BusinessOnlyWidgetState {
  instanceId: string;
  alreadySentAwayMessage: boolean;
  shouldShowChat: boolean;
  alreadySentLcf: boolean;
}

class BusinessOnlyWidget extends React.Component<
  BusinessOnlyWidgetProps,
  BusinessOnlyWidgetState
> {
  constructor(props: BusinessOnlyWidgetProps) {
    super(props);
    this.state = {
      instanceId: wix.getInstanceId(),
      shouldShowChat: this.shouldShowChat(props.visibilitySettings),
      alreadySentAwayMessage: false,
      alreadySentLcf: false,
    };
  }

  shouldShowChat = ({ publicVisibility, chatOnboarded, editorVisibility }) =>
    this.isInSiteView() ? publicVisibility && chatOnboarded : editorVisibility;

  async componentDidMount() {
    Sentry.addBreadcrumb({
      category: 'MountedWidget',
      message: 'BusinessOnly',
    });
    expandCollapseManager.registerOnExpand(this.handleExpand);
    expandCollapseManager.registerOnCollapse(this.handleCollapse);

    this.showOrHideChat();
    await this.props.initApp({});

    void loadingScheduler.onInteractive().then(() => {
      if (this.props.hasMessages) {
        this.onMessageReceived();
      }
    });
  }

  componentWillUnmount() {
    expandCollapseManager.unregisterOnExpand(this.handleExpand);
    expandCollapseManager.unregisterOnCollapse(this.handleCollapse);
    this.props.firebaseGoOffline();
    this.props.updateHasMessages(false);
  }

  showOrHideChat() {
    if (!this.state.shouldShowChat) {
      void this.hide();
    }
  }

  isInSiteView = () => this.props.viewMode === EditorViewMode.SITE;

  isMobile = () => this.props.deviceType === 'mobile';

  handleExpand = async ({ direction = 'uou', referrer = 'widget' } = {}) => {
    biLogger.expandChatEvent({ direction, referrer });
    await this.props.expandWidget();

    this.onPostExpand();
  };

  onPostExpand() {
    this.props.firstExpand();
    this.handleSendAwayMessage();
  }

  handleSendAwayMessage() {
    if (
      !this.state.alreadySentAwayMessage &&
      this.props.availabilityStatus === AvailabilityStatus.Away &&
      !this.props.hasMessages &&
      this.props.isContact
    ) {
      const {
        editorSettingsTexts: { offlineChatMessage },
      } = this.props;

      const messageDto = Message.fromText({
        text: offlineChatMessage,
        chatroomId: this.props.businessChatRoomId,
        userId: 'business',
        summary: '',
      });

      void chatSdk.addMessage(messageDto);
    }

    this.setState({ alreadySentAwayMessage: true });
  }

  handleCollapse = () => !this.isMobile() && this.props.collapseWidget();

  triggerCollapse = () => expandCollapseManager.collapse();

  hide() {
    return wix.resizeToHidden();
  }

  handleOnClick = () => {
    if (this.isInSiteView()) {
      firebaseChatService.goOnlineIfOffline();
    }
  };

  onMessageReceived = () => {
    this.props.updateHasMessages(true);

    biLogger.messageReceivedEvent(this.props.shardId);
    this.setState({ shouldShowChat: true });

    if (this.isMobile()) {
      this.props.onMessageReceived();
    } else if (
      !this.props.widgetExpanded &&
      (!this.props.experiments.enabled('specs.chat.MessageNotification') ||
        (this.props.experiments.enabled('specs.chat.MessageNotification') &&
          this.props.editorSettingsBehaviour.onNewMessage !==
            ON_NEW_MESSAGE.SHOW_NOTIFICATION))
    ) {
      void this.handleExpand({ direction: 'user', referrer: 'widget' });
    }
  };

  onSendMessage = message => {
    wix.publishToPubSub(ViewerOnMessageSentEvent, message);
    return biLogger.sendMessageEvent();
  };
  onAttachmentClick = () => biLogger.attachmentClickEvent();
  onCallToActionClick = url => {
    biLogger.callToActionClickEvent(url);
    this.openProductPage(url);
  };

  openProductPage = url => {
    this.isMobile() && this.triggerCollapse();
    const productRegex = /product-page\/([^?/\s]+)/gm;
    const productMatch = productRegex.exec(url);
    const fallbackBehavior = () => window.open(url);
    if (productMatch) {
      const productName = productMatch[1];
      wix.navigateToSection(
        {
          appDefinitionId: CTA_PRODUCT.APP_DEFINITION_ID,
          sectionId: CTA_PRODUCT.SECTION_ID,
        },
        productName,
        fallbackBehavior,
      );
    } else {
      fallbackBehavior();
    }
  };

  onChatDisplayed = () => {
    if (this.state.shouldShowChat) {
      const asRounded = _get(
        this.props.editorSettingsLayout,
        'collapsedAsRounded',
        false,
      );
      const layoutName = asRounded
        ? 'collapsedAsRounded'
        : 'selectedRadiusOption';

      biLogger.chatDisplayedEvent(this.props.deviceType, layoutName);
    }
  };

  handleUploadFile = async file => {
    const { token, url, folderId } = await serverApi.getUploadToken();
    const isNewGen = this.isNewGenMediaEnabled();
    const effectiveFolderId = isNewGen ? folderId : undefined;
    return mediaManager.uploadFileToMedia({
      file,
      token,
      uploadUrlApi: url,
      folderId: effectiveFolderId,
    });
  };

  isNewGenMediaEnabled = () => {
    return (
      this.props.experiments.enabled('specs.media.MediaManager3') &&
      this.props.experiments.enabled('specs.crm.WixChatUsingMM3Spec')
    );
  };

  eventHooks = {
    onSendMessage: message => this.onSendMessage(message),
    onMessageReceived: () => this.onMessageReceived(),
    onAttachmentClick: () => this.onAttachmentClick(),
    onChatDisplayed: () => this.onChatDisplayed(),
    onCallToActionClick: url => this.onCallToActionClick(url),
  };

  onMessageNotificationClick = () => {
    expandCollapseManager.expand({
      direction: 'uou',
      referrer: 'message indication',
    });
  };

  render() {
    const { shouldShowChat, instanceId } = this.state;
    const {
      themeData,
      locale,
      visibilitySettings,
      isChatSdkInit,
      businessChatRoomId,
      availabilityStatus,
      isBranded,
      widgetExpanded,
      widgetMode,
      unreadCount,
    } = this.props;
    const {
      editorSettingsTexts: {
        onlineTitle,
        onlineMessage,
        offlineTitle,
        offlineMessage,
      },
      editorSettingsBehaviour: { responseTimeEnabled, responseTimeSelected },
    } = this.props;
    const avatar = this.props.editorSettingsAvatar;
    const dummyMode = this.isInSiteView() && !visibilitySettings.chatOnboarded;
    const isMobile = this.isMobile();
    const isAvailable = availabilityStatus !== AvailabilityStatus.Away;
    const typicallyReplies = {
      isEnabled: responseTimeEnabled,
      period: responseTimeSelected,
    };
    const shouldRender = isChatSdkInit || widgetMode === 'site';
    return shouldRender ? (
      <div onClick={this.handleOnClick}>
        {this.props.isMessageNotificationEnabled && (
          <MessagePopup
            onClick={this.onMessageNotificationClick}
            activeRoom={businessChatRoomId}
          />
        )}
        <ChatWidget
          isExpanded={isMobile || widgetExpanded}
          theme={themeData}
          onlineTitle={isAvailable ? onlineTitle : offlineTitle}
          onlineMessage={isAvailable ? onlineMessage : offlineMessage}
          avatar={avatar}
          eventHooks={this.eventHooks}
          available={shouldShowChat}
          isMobile={isMobile}
          locale={locale}
          availabilityStatus={availabilityStatus || undefined}
          unreadCount={unreadCount}
          typicallyReplies={typicallyReplies}
        >
          <LazyChatRoomScreen
            chatroomId={businessChatRoomId}
            dummyMode={dummyMode}
            onUploadFile={this.handleUploadFile}
            instanceId={instanceId}
            banner={isBranded ? true : undefined}
            unfoldWhenEmpty={isAvailable}
            appMode={AppMode.Widget}
            locale={locale}
            theme={themeData}
            avatarOption={AvatarConfig.NONE}
          />
        </ChatWidget>
      </div>
    ) : null;
  }
}

function mapStateToProps(state) {
  return {
    themeData: theme(state),
    editorSettingsAvatar: editorSettingsAvatar(state),
    editorSettingsTexts: state.editorSettingsTexts,
    editorSettingsBehaviour: state.editorSettingsBehaviour,
    editorSettingsLayout: state.editorSettingsLayout,
    visibilitySettings: state.visibilitySettings,
    viewMode: state.viewMode,
    locale: state.locale,
    shardId: state.shardId,
    businessChatRoomId: state.businessChatRoomId,
    isChatSdkInit: state.isChatSdkInit,
    deviceType: state.deviceType,
    availabilityStatus: state.availabilityStatus,
    isBranded: state.isBranded,
    widgetExpanded: state.widgetExpanded,
    isContact: state.isLcfMandatory.isContact,
    hasMessages: state.hasMessages,
    widgetMode: state.widgetMode,
    unreadCount: state.unreadCount,
    isMessageNotificationEnabled: isMessageNotificationEnabled(state),
  };
}

const mapDispatchToProps = dispatch => ({
  firebaseGoOffline: () => dispatch(goOffline()),
  initApp: data => dispatch(initApp(data)),
  expandWidget: () => dispatch(expandWidget()),
  collapseWidget: () => dispatch(collapseWidget()),
  firstExpand: () => dispatch(firstExpand()),
  updateHasMessages: hasMessages => dispatch(updateHasMessages(hasMessages)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withExperiments(withMobile(BusinessOnlyWidget)));
